import { ContinueShoppingLink } from "@/components/cart/ContinueShoppingLink";
import { useFloatingButtonsLift } from "@/components/support/useFloatingButtonsLift";
import { formatPrice } from "@/utils/money";
import { Button } from "@medusajs/ui";
import type { SalonStockerHttpTypes } from "@repo/api-sdk";
import { memo } from "react";

type StoreSellerCartsSummary = SalonStockerHttpTypes.StoreSellerCartsSummary;

export interface MatrixCheckoutBarProps {
  /** Aggregated totals across the carts being compared. */
  summary: StoreSellerCartsSummary;
  /**
   * Proceed to the internal checkout. Omit to hide the button, e.g. when any
   * of the buyer's suppliers checks out on its own store and there is no
   * combined checkout to offer.
   */
  onCheckout?: () => void;
  /** Whether checkout is disabled (nothing selected). */
  isCheckoutDisabled?: boolean;
}

/**
 * The Compare view's way out: the grand total and, when `onCheckout` is given,
 * the Proceed-to-checkout action, pinned to the bottom of the page's scroll
 * area so checkout is never a scroll away from a wide grid. The Compare view
 * has no summary rail, so without this a salon who has finished comparing has
 * to switch back to the Cart view just to pay.
 *
 * `md` and up only: below `md` the page mounts `MobileCartSummaryBar` instead,
 * which is the same total + action as a fixed bar. This one is `sticky` rather
 * than `fixed` so it stays inside the content column beside the sidebar.
 */
export const MatrixCheckoutBar = memo(function MatrixCheckoutBar({
  summary,
  onCheckout,
  isCheckoutDisabled = false
}: MatrixCheckoutBarProps) {
  // Proceed must not sit under the support FABs. CartPage keeps this bar out of
  // their corner (the column reserves FLOATING_BUTTONS_GUTTER_PX), so this reads 0
  // there and the FABs stay beside the bar rather than riding over the grid. It
  // remains as the safety net if the bar is ever mounted where it does reach
  // the corner.
  const barRef = useFloatingButtonsLift<HTMLDivElement>();

  return (
    <div
      ref={barRef}
      className="sticky bottom-0 z-10 mt-3 flex items-center justify-end gap-6 rounded-xl border border-gray-200 bg-white px-4 py-2 shadow-[0_-4px_16px_-8px_rgba(0,0,0,0.2)]"
    >
      {/* Secondary, then the total, then the primary: the total is the price
          the black button commits to, so it sits right beside it with the
          secondary link outermost. Grouped on the right so the total also stays
          clear of the bottom-left, where the supplier-switch toast lands. */}
      <ContinueShoppingLink />

      <div className="min-w-0">
        <span className="block text-xs text-gray-500">Total</span>
        <span className="block text-base font-bold text-gray-900">
          {formatPrice(summary.grand_total)}
        </span>
      </div>

      {onCheckout && (
        <Button
          size="large"
          onClick={onCheckout}
          disabled={isCheckoutDisabled}
          className="whitespace-nowrap"
        >
          Proceed to checkout
        </Button>
      )}
    </div>
  );
});
